import type { DashboardId } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, bars, lines, list, type Rect } from '../charts.ts';
import { subjectDetail } from '../details.ts';
import { StudyView } from '../StudyView.ts';

/**
 * Enrollment & Recruitment: enrolled vs target, screen-failure rate, median consent to
 * randomisation; 3D cumulative curve rising off the panel; bars per site; site -> subjects.
 */
export class EnrollmentDashboard extends Dashboard {
  readonly id: DashboardId = 'Enrollment';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('site:')) return this.view.siteLabel(segment.slice(5));
    if (segment.startsWith('subject:')) return segment.slice(8);
    return segment;
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const d = this.view.file.dashboards.enrollment;
    const [site, subject] = this.path;
    if (site && subject) {
      const s = this.view.subject(subject.slice(8));
      if (s) {
        this.detail = subjectDetail(this.view, s);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    if (site) {
      const siteId = site.slice(5);
      const subjects = this.view.subjects({ ...this.filters, siteId });
      const rows = list(
        ctx,
        ['Subject', 'Status', 'Consent', 'Randomised', 'Arm'],
        subjects.map((s) => ({
          cells: [s.id, s.status, s.consentDate, s.randomisationDate ?? '—', s.arm ?? '—'],
          flag: s.status === 'Screen Failed' ? 'sf' : undefined,
          colour: PALETTE.grey,
        })),
        area,
        {
          widths: [0.18, 0.22, 0.2, 0.2, 0.2],
          title: `${this.view.siteLabel(siteId)} · ${subjects.length} subjects`,
          hoveredIndex: hoveredRow(panel, 'row:'),
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `subject:${subjects[i]!.id}` },
          tooltip: [`${subjects[i]!.id}`, `${subjects[i]!.status} · open the record`],
        }),
      );
      return;
    }
    const filtered = StudyView.isFiltered(this.filters);
    const subjects = this.view.subjects(this.filters);
    const enrolled = subjects.filter((s) => s.randomisationDate).length;
    const screened = subjects.length;
    const sf = subjects.filter((s) => s.status === 'Screen Failed').length;
    const k = d.kpis;
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'Enrolled / target',
          value: filtered ? `${enrolled} / ${k.target}` : `${k.enrolled} / ${k.target}`,
          colour: PALETTE.teal,
          sub: `${Math.round(((filtered ? enrolled : k.enrolled) / Math.max(1, k.target)) * 100)}% of target`,
        },
        {
          label: 'Screen-failure rate',
          value: `${filtered ? (screened ? Math.round((sf / screened) * 1000) / 10 : 0) : k.screenFailureRate}%`,
          sub: `${filtered ? sf : k.screenFailed} of ${filtered ? screened : k.screened} screened`,
        },
        {
          label: 'Consent to randomisation',
          value:
            k.medianDaysConsentToRandomisation !== null
              ? `${k.medianDaysConsentToRandomisation} d`
              : '—',
          sub: 'median',
        },
        {
          label: 'Sites',
          value: String(filtered && this.filters.siteId ? 1 : d.perSite.length),
          sub: 'active',
        },
      ],
      { x: area.x, y: area.y, w: area.w, h: 84 },
    );
    const left: Rect = { x: area.x, y: area.y + 96, w: area.w * 0.56, h: area.h - 96 };
    const right: Rect = {
      x: area.x + area.w * 0.6,
      y: area.y + 96,
      w: area.w * 0.4,
      h: area.h - 96,
    };
    const labels = d.cumulative.map((c) => c.weekEnding.slice(5));
    const cols = lines(
      ctx,
      [
        {
          values: d.cumulative.map((c) => c.actual),
          colour: PALETTE.teal,
          fill: true,
          label: 'Actual',
        },
        {
          values: d.cumulative.map((c) => c.planned),
          colour: PALETTE.amber,
          dashed: true,
          label: 'Planned',
        },
      ],
      labels,
      left,
      {
        title: 'Cumulative enrollment',
        max: Math.max(k.target, 1),
        hoveredIndex: hoveredRow(panel, 'week:'),
      },
    );
    cols.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `week:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [
          `Week ending ${d.cumulative[i]!.weekEnding}`,
          `Actual ${d.cumulative[i]!.actual} · planned ${d.cumulative[i]!.planned}`,
        ],
        selectable: false,
      }),
    );
    const siteBars = d.perSite.map((s) => ({
      label: this.view.site(s.siteId)?.city ?? s.siteId,
      values: [s.enrolled, s.target - s.enrolled],
      colours: [PALETTE.teal, PALETTE.track],
    }));
    const rects = bars(ctx, siteBars, right, {
      stacked: true,
      title: 'Enrolled per site (vs target)',
      hoveredIndex: hoveredRow(panel, 'site:'),
    });
    rects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'drill', segment: `site:${d.perSite[i]!.siteId}` },
        tooltip: [
          this.view.siteLabel(d.perSite[i]!.siteId),
          `${d.perSite[i]!.enrolled} enrolled of ${d.perSite[i]!.target} · ${d.perSite[i]!.screenFailed} screen failures`,
          'Open the subject list',
        ],
      }),
    );
  }
}

export function hoveredRow(panel: CanvasPanel, prefix: string): number {
  const h = panel.hoveredRegion?.id;
  return h && h.startsWith(prefix) ? Number(h.slice(prefix.length)) : -1;
}
